import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import Index from "./pages/Index.tsx";
import ScrollToTop from "./components/ScrollToTop.tsx";
import PageTransitionOverlay from "./components/PageTransitionOverlay.tsx";
import { useCartSync } from "./hooks/useCartSync";

// Lazy-load all non-homepage routes to keep the initial JS bundle small.
// The homepage (Index) remains eager-loaded so first paint is unaffected.
const Archive = lazy(() => import("./pages/Archive.tsx"));
const Product = lazy(() => import("./pages/Product.tsx"));
const Sold = lazy(() => import("./pages/Sold.tsx"));
const Sell = lazy(() => import("./pages/Sell.tsx"));
const EmailList = lazy(() => import("./pages/EmailList.tsx"));
const Admin = lazy(() => import("./pages/Admin.tsx"));
const AdminBrands = lazy(() => import("./pages/AdminBrands.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions.tsx"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.tsx"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy.tsx"));
const ShippingReturns = lazy(() => import("./pages/ShippingReturns.tsx"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy.tsx"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

const AppContent = () => {
  useCartSync();
  return (
    <>
      <ScrollToTop />
      <PageTransitionOverlay />
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/archive" element={<Archive />} />
          <Route path="/product/:handle" element={<Product />} />
          <Route path="/sold" element={<Sold />} />
          <Route path="/sell" element={<Sell />} />
          <Route path="/email-list" element={<EmailList />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/brands" element={<AdminBrands />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/shipping-returns" element={<ShippingReturns />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <CurrencyProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </TooltipProvider>
      </CurrencyProvider>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
